import { makeFormData } from "utils/makeFormData";
import { emptyApi } from "./emptyApi";
import { Invoice } from "types/Models/invoice";
import { AuditLog } from "./auditlogs";
import {
  CompanyTasksCounts,
  CompanyTasksOverview,
  CompanyUnlaunchedTasks,
  EntityWithSeasonAndCount,
  TaskDeadlineOverviewItem,
  TaskDeadlineOverviewResponse,
  TaskListBaseQueryParams,
  TaskListPerEntityPerSeason,
  TaskListPerEntityPerSeasonParams,
} from "types/Models/task";
import qs from "qs";
import { AddToCartPayload } from "./products";
import { AddFilingToCartPayload } from "types/Models/Filing";
import * as FILE_TYPE from "constants/fileTypes";

export type fillingCountItem = {
  task__season: number;
  count: number;
};

type FilingSummary = {
  uuid: string | null;
  name: string | null;
  file_type: keyof typeof FILE_TYPE | null;
  is_previewable: boolean | null;
};

export type FilingTask = {
  task_id: string;
  task_status: string | null;
  is_final: boolean;
  channel_url: string | null;
  preparer: {
    uuid: string | null;
    name: string | null;
  };
};

export type TaxFiling = {
  uuid: string;
  state: string;
  payment_schedule: string;
  filing_period_start_date: string;
  filing_period_end_date: string;
  filing_due_date: string;
  total_sales: number;
  total_tax_collected: number;
  filing_summary: FilingSummary;
  status: "NOT_STARTED" | "IN_CART" | "PROCESSING" | "FILED" | "SUBSCRIBED";
  invoice_count?: number;
  deadline?: string;
  login_email?: string;
  preparer?: {
    user_service_team_map_id: string;
    name: string;
  };
  service_team?: {
    uuid?: string;
    name?: string;
  };
  assigned_on?: string;
  task: FilingTask | null;
  is_in_cart: boolean;
  filing_price: number | null;
};

type readyToFilePayload = {
  filing_ids: string;
};

export type FilingTransactionResponse = {
  transactions: Invoice[];
  total_pages: number;
  current_page: number;
  total_count: number;
  per_page: number;
};

type FilingResponseByStateId = {
  uuid: string;
  state: string;
  filing_due_date: string;
  filing_tax_year: number;
};

export const PAYMENT_SCHEDULE = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  ANNUALLY: "ANNUALLY",
  SEMI_ANNUALLY: "SEMI_ANNUALLY",
} as const;

export type PaymentSchedule =
  (typeof PAYMENT_SCHEDULE)[keyof typeof PAYMENT_SCHEDULE];
type LaunchFilingStateResponse = {
  name: string;
  uuid: string;
  payment_schedule: PaymentSchedule;
};

export type LaunchFilingPayload = {
  filing_season: number;
  filing_schedule: string;
  filing_periods: { from: string; to: string | null }[];
};

export const fillings = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getFilingCount: build.query<
      fillingCountItem[],
      {
        entityId: string;
        status: "all" | "action_required" | "in_progress" | "completed";
        searchTerm?: string | null;
      }
    >({
      query: ({ entityId, status, searchTerm }) => {
        const searchParam = searchTerm
          ? `&search_term=${encodeURIComponent(searchTerm)}`
          : "";
        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/count/?${status}=True${searchParam}`,
        };
      },
      providesTags: ["FILINGS"],
    }),

    getAllFilings: build.query<
      TaxFiling[],
      {
        entityId: string;
        status: "all" | "action_required" | "in_progress" | "completed";
        seasons?: number | string;
        searchTerm?: string | null;
      }
    >({
      query: ({ entityId, status, seasons, searchTerm }) => {
        const searchParam = searchTerm
          ? `&search_term=${encodeURIComponent(searchTerm)}`
          : "";
        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/?${status}=True&seasons=${seasons}${searchParam}`,
        };
      },
      providesTags: ["FILINGS"],
    }),

    getFilingDetails: build.query<
      TaxFiling,
      {
        entityId: string;
        fillingId: string;
      }
    >({
      query: ({ entityId, fillingId }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/${fillingId}`,
        };
      },
      providesTags: ["FILINGS_DETAILS"],
    }),
    readyToFile: build.mutation<
      {},
      { entityId: string; payload: readyToFilePayload }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["FILINGS", "FILINGS_DETAILS"],
    }),

    startFiling: build.mutation<
      { uuid: string; group_id: string },
      { entityId: string; filingId: string }
    >({
      query: ({ entityId, filingId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/filings/${filingId}/start/`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result ? ["FILINGS", "FILINGS_DETAILS", "CART"] : [],
    }),

    startFilingAndSubscribe: build.mutation<
      { uuid: string; group_id: string },
      { entityId: string; filingId: string }
    >({
      query: ({ entityId, filingId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/filings/${filingId}/start-and-subscribe/`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result ? ["FILINGS", "FILINGS_DETAILS", "CART"] : [],
    }),

    recalculateAllFilingTotals: build.mutation<null, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/filings/recalculate/`,
        method: "POST",
      }),
      invalidatesTags: ["FILINGS", "FILINGS_DETAILS"],
    }),

    markFilingsFiled: build.mutation<
      { updated_filing_ids: string[] },
      { entityId: string; filingIds: string[] }
    >({
      query: ({ entityId, filingIds }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/filings/mark-filed/`,
        method: "POST",
        body: { filing_ids: filingIds },
      }),
      invalidatesTags: ["FILINGS", "FILINGS_DETAILS"],
    }),

    markFilingFiledWithCopy: build.mutation<
      { updated_filing_id: string },
      { entityId: string; filingId: string; filedCopyFile?: File | null }
    >({
      query: ({ entityId, filingId, filedCopyFile }) => {
        const formData = new FormData();
        if (filedCopyFile) {
          formData.append("filed_copy", filedCopyFile);
        }

        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/${filingId}/mark-filed-with-copy/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["FILINGS", "FILINGS_DETAILS"],
    }),

    getFilingTransactions: build.query<
      FilingTransactionResponse,
      {
        entityId: string;
        filingId: string;
        pageNum?: number;
      }
    >({
      query: ({ entityId, filingId, pageNum = 1 }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/${filingId}/transactions/?page_name=${pageNum}`,
        };
      },
      providesTags: ["FILINGS"],
    }),
    getFilingDetailsByStateId: build.query<
      FilingResponseByStateId,
      {
        entityId: string;
        stateId: string;
      }
    >({
      query: ({ entityId, stateId }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/state/${stateId}/action_required/`,
        };
      },
    }),
    getLaunchFilingState: build.query<
      LaunchFilingStateResponse[],
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/states/?status=REGISTERED`,
        };
      },
    }),

    launchFiling: build.mutation<
      {},
      { entityId: string; stateId: string; payload: LaunchFilingPayload }
    >({
      query: ({ entityId, stateId, payload }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/filings/state/${stateId}/launch/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["FILINGS"],
    }),
    getFilingAndRegistrationCount: build.query<
      { filing_count: number; registration_count: number },
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/tasks/count/`,
        };
      },
    }),
    getAllFilingsAndDeadlines: build.query<
      TaskDeadlineOverviewResponse,
      {
        entityId: string;
        groupId: string;
        filing_season: string;
        dashboard_status_filter: string | null;
        search_term: string | null;
        page_num?: number | null;
        task_category_type:
          | "TAX,RND"
          | "BOOKS"
          | "INTRA_GROUP"
          | "SALES_TAX"
          | null;
      }
    >({
      query: ({ entityId, groupId, ...props }) => {
        let queryUrl = qs.stringify(props, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/tasks/group/${groupId}/entity/${entityId}/task-deadlines-overview/${queryUrl}`,
        };
      },
      providesTags: (_result, _error) => [{ type: "TaskList" }],
    }),
    getAllTasks: build.query<
      CompanyTasksOverview,
      {
        groupId: string;
        filing_seasons: string | null;
        status_filter?: string | null;
        base_task_templates?: string | null;
        deadline_start?: string | null;
        deadline_end?: string | null;
        action_pending_on_user?: boolean | null;
        search_term?: string | null;
      }
    >({
      query: ({ groupId, ...props }) => {
        let queryUrl = qs.stringify(props, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/tasks/group/${groupId}/task-deadlines-overview/${queryUrl}`,
        };
      },
      providesTags: (_result, _error) => [{ type: "TaskList" }],
    }),

    getTaskSeasons: build.query<
      { seasons: string[] },
      {
        groupId: string;
      }
    >({
      query: ({ groupId }) =>
        `/api/inkle/tasks/group/${groupId}/task-deadlines-overview/available-seasons/`,
    }),

    markAsNotRequired: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        payload: AddFilingToCartPayload;
      }
    >({
      query: ({ groupId, payload, entityId }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart/add-and-archive/`,
        method: "POST",
        body: makeFormData(payload),
      }),
      invalidatesTags: (res) => (res ? ["TaskList"] : []),
    }),

    getAllUnlaunchedTasks: build.query<
      CompanyUnlaunchedTasks,
      {
        groupId: string;
        season: string | null;
      }
    >({
      query: ({ groupId, ...props }) => {
        let queryUrl = qs.stringify(props, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/billing/group/${groupId}/not-purchased-filings/${queryUrl}`,
        };
      },
      providesTags: (_result, _error) => [{ type: "UNLAUNCHED_TASK_LIST" }],
    }),
    getAllTaskSeasons: build.query<
      {
        entities: EntityWithSeasonAndCount[];
      },
      TaskListBaseQueryParams & { filing_seasons: string | null }
    >({
      query: ({ groupId, ...props }) => {
        let queryUrl = qs.stringify(props, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/tasks/group/${groupId}/entity-data-available-seasons/${queryUrl}`,
        };
      },
    }),
    getAllTasksByEntity: build.query<
      TaskListPerEntityPerSeason,
      TaskListPerEntityPerSeasonParams
    >({
      query: ({ groupId, entity_id, ...props }) => {
        let queryUrl = qs.stringify(props, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/tasks/group/${groupId}/entity/${entity_id}/task_deadlines/entity_data/${queryUrl}`,
        };
      },
      providesTags: (_result, _error) => [{ type: "TaskList" }],
    }),

    getTaskActivity: build.query<
      { logs: AuditLog[]; total_count: number },
      { groupId: string; entityId: string; taskId: string }
    >({
      query: ({ groupId, entityId, taskId }) => ({
        url: `/api/inkle/tasks/group/${groupId}/entity/${entityId}/task/${taskId}/activity/`,
      }),
      providesTags: (_result, _error) => [{ type: "TaskList" }],
    }),

    getTasksStats: build.query<
      CompanyTasksCounts,
      TaskListBaseQueryParams & {
        filing_seasons: string | null;
      }
    >({
      query: ({ groupId, ...props }) => {
        let queryUrl = qs.stringify(props, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/tasks/group/${groupId}/task_deadlines/counts/${queryUrl}`,
        };
      },
      providesTags: (_result, _error) => [{ type: "TaskList" }],
    }),
  }),
});

export const {
  useGetFilingCountQuery,
  useGetAllFilingsQuery,
  useGetFilingDetailsQuery,
  useReadyToFileMutation,
  useStartFilingMutation,
  useStartFilingAndSubscribeMutation,
  useRecalculateAllFilingTotalsMutation,
  useMarkFilingsFiledMutation,
  useMarkFilingFiledWithCopyMutation,
  useGetFilingTransactionsQuery,
  useGetFilingDetailsByStateIdQuery,
  useLazyGetFilingDetailsByStateIdQuery,
  useGetLaunchFilingStateQuery,
  useLaunchFilingMutation,
  useGetFilingAndRegistrationCountQuery,
  useGetAllFilingsAndDeadlinesQuery,
  useGetAllTasksQuery,
  useMarkAsNotRequiredMutation,
  useGetTaskSeasonsQuery,
  useGetAllUnlaunchedTasksQuery,
  useLazyGetAllUnlaunchedTasksQuery,
  useGetAllTaskSeasonsQuery,
  useGetAllTasksByEntityQuery,
  useGetTaskActivityQuery,
  useGetTasksStatsQuery,
  usePrefetch: usePrefetchFilingEndpoints,
} = fillings;
